/**
 * ClockCorrectionControls — admin/client fix-ups for an accidental
 * early clock-out.
 *
 * Two actions, shown only when the talent is CLOCKED OUT and the
 * timesheet is NOT yet approved/paid:
 *   • Undo Checkout / Reopen Shift  → POST /timesheet-edit/{id}/reopen
 *       Restores the talent to "Clocked In" so they can keep working
 *       and clock out normally later. The premature checkout is kept in
 *       the audit history as "Reversed by Admin" (never deleted).
 *   • Edit Time → reuses <TimesheetEditModal> (PATCH /timesheet-edit/{id})
 *
 * Non-destructive + fully audited on the backend. Embedded on both the
 * Attendance/Clock-In roster (AdminShiftDetail) and the individual
 * booking/timesheet screen (OperationsDashboard).
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RotateCcw, Clock3, Loader2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import TimesheetEditModal from './TimesheetEditModal';

export default function ClockCorrectionControls({
  booking,
  onDone,
  showEdit = true,
  showUndo = true,
  compact = false,
}) {
  const [undoOpen, setUndoOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  if (!booking) return null;

  const bookingId = booking.booking_id || booking.id;
  const clockedOut = !!(booking.clock_out_time || booking.clock_out_iso || booking.clock_out);
  const locked =
    booking.hours_approved === true ||
    !!booking.timesheet_approved_at ||
    ['timesheet_approved', 'paid'].includes(booking.booking_status || booking.status);

  // Nothing to correct unless they're clocked out and still editable.
  if (!clockedOut || locked) return null;

  const doReopen = async () => {
    setSaving(true);
    try {
      await api.post(`/timesheet-edit/${bookingId}/reopen`, { reason: reason.trim() || undefined });
      toast.success('Shift reopened — talent is Clocked In again');
      setUndoOpen(false);
      setReason('');
      onDone?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'object' ? (d.message || d.code) : (d || 'Failed to reopen shift'));
    } finally {
      setSaving(false);
    }
  };

  // Normalize a row shape TimesheetEditModal understands.
  const editRow = {
    booking_id: bookingId,
    clock_in_iso: booking.clock_in_iso || booking.clock_in_time || null,
    clock_out_iso: booking.clock_out_iso || booking.clock_out_time || null,
    original_clock_in: booking.original_clock_in || null,
    original_clock_out: booking.original_clock_out || null,
    timezone: booking.timezone || null,
    state: booking.state || null,
    break_minutes: booking.break_minutes || 0,
    pay_rate: booking.pay_rate ?? booking.talent_pay_rate ?? booking.effective_pay_rate ?? 0,
    talent_pay_rate: booking.talent_pay_rate ?? null,
    talent_name: booking.talent_name || booking.name || '',
    event_name: booking.event_name || booking.venue_name || '',
    shift_date: booking.shift_date || '',
  };

  const btnBase = `inline-flex items-center justify-center gap-1 rounded-md border font-medium transition-colors ${
    compact ? 'text-[11px] px-2 py-1' : 'text-xs px-2.5 py-1.5'
  }`;

  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`clock-correction-${bookingId}`}>
      {showUndo && (
        <button
          type="button"
          onClick={() => setUndoOpen(true)}
          className={`${btnBase} text-amber-700 bg-amber-50 hover:bg-amber-100 border-amber-200`}
          data-testid={`undo-checkout-btn-${bookingId}`}
          title="Talent clocked out early by mistake — put them back on the clock"
        >
          <Undo2 className="w-3 h-3" /> Undo Checkout
        </button>
      )}
      {showEdit && (
        <button
          type="button"
          onClick={() => setEditOpen(true)}
          className={`${btnBase} text-cyan-700 bg-cyan-50 hover:bg-cyan-100 border-cyan-200`}
          data-testid={`edit-clock-time-btn-${bookingId}`}
          title="Correct the recorded clock-out time"
        >
          <Clock3 className="w-3 h-3" /> Edit Time
        </button>
      )}

      {/* Undo confirm dialog */}
      <Dialog open={undoOpen} onOpenChange={(v) => (!v && !saving) && setUndoOpen(false)}>
        <DialogContent className="max-w-md" data-testid={`undo-checkout-dialog-${bookingId}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <RotateCcw className="w-4 h-4 text-amber-600" /> Reopen Shift
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-gray-700">
            <p>
              This reverses the clock-out for{' '}
              <strong>{editRow.talent_name || 'this talent'}</strong> and sets them back to{' '}
              <strong>Clocked In</strong>. They can keep working and clock out normally at the real
              end of the shift.
            </p>
            <p className="text-[12px] text-gray-500">
              The premature checkout is kept in the audit history as “Reversed by Admin.” Nothing is
              deleted.
            </p>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-gray-500">
                Reason (optional)
              </Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder="e.g. Talent tapped clock-out by mistake; still on site."
                data-testid={`undo-checkout-reason-${bookingId}`}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setUndoOpen(false)} disabled={saving}
              data-testid={`undo-checkout-cancel-${bookingId}`}>
              Cancel
            </Button>
            <Button onClick={doReopen} disabled={saving}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid={`undo-checkout-confirm-${bookingId}`}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Undo2 className="w-4 h-4 mr-1" />}
              Reopen &amp; Clock Back In
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showEdit && (
        <TimesheetEditModal
          open={editOpen}
          row={editRow}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); onDone?.(); }}
        />
      )}
    </div>
  );
}
